import moment from "moment";
import * as _ from "lodash";
import EditCodes from "@/enums/EditCodes";
import EventTypes from "@/enums/EventTypes";
import EventStatus from "@/enums/EventStatus";
import BlockType from "@/enums/BlockType";
import PlaylistTypes from "@/enums/PlaylistTypes";

const cutCopyPasteMixin = {
	computed: {
		copiedEvent() {
			return this.$store.getters.getCopiedEvent();
		},
		cuttingEvent() {
			return this.$store.getters.getCuttingEvent();
		}
	},

	methods: {
		cutEvent(event) {
			const playlist = this.$store.getters.getPlaylistById(
				{stationID: this.selectedStationID, playlistID: event.PlaylistID}
			);
			// keep the playlist in case we paste the event in another playlist and the current gets unloaded from the state
			this.$store.commit("setCuttingEvent", {...event, Playlist: playlist});
			this.$store.commit("setCopiedEvent", null);
		},

		copyEvent(event) {
			this.$store.commit("setCopiedEvent", event);
			this.$store.commit("setCuttingEvent", null);

		},

		pasteEvent(event, $el) {
			const sourceEvent = this.cuttingEvent ?? this.copiedEvent;

			const eventStatus = this.$store.getters.getEventStatus({event});
			if (eventStatus.isPast) {
				this.$store.commit(
					"SHOW_NOTIFICATION_WITH_TYPE",
					{message: "You cannot paste an event on a past airtime."}
				);

				this.$store.commit("setCuttingEvent", null);
				this.$store.commit("setCopiedEvent", null);
				return;
			}

			// A spot block cannot be pasted inside another spot block.
			const isPastingIntoSpotBlock = $el.classList.contains("spot-block") || $el.classList.contains("spot");
			const isPastingFromSpotBlock = this.$store.getters.getPlaylistById(sourceEvent.PlaylistID)?.PlaylistType === PlaylistTypes.SpotBlock;
			const moveSpotFeatureEnabled = this.$store.getters.isFeatureEnabled({
				object_id: this.selectedStationID,
				reference: "zettacloud_event_move_spot"
			});
			if (sourceEvent.EventType === EventTypes.SpotBlock && isPastingIntoSpotBlock) {
				this.$store.commit(
					"SHOW_NOTIFICATION_WITH_TYPE",
					{message: "A spot block cannot be pasted inside another spot block."}
				);
				this.$store.commit("setCuttingEvent", null);
				this.$store.commit("setCopiedEvent", null);
				return;
			}
			else if (!moveSpotFeatureEnabled && (isPastingFromSpotBlock || isPastingIntoSpotBlock)) {
				this.$store.commit("SHOW_NOTIFICATION_WITH_TYPE", {
					message: "You can't move spots. The move spot feature is not enabled."
				});
				this.$store.commit("setCuttingEvent", null);
				this.$store.commit("setCopiedEvent", null);
				return;
			}
			if (this.cuttingEvent) {
				this.pasteAfterCut(event);
			}
			else {
				this.pasteAfterCopy(event);
			}
		},

		async pasteAfterCut(targetEvent) {
			this.$store.commit("setCuttingEvent", {...this.cuttingEvent, blink: true});

			// Cut a spot block
			const newSequenceNumber = await this.$store.dispatch("generateSequenceNumber", {
				eventID: targetEvent.EventID,
				afterEvent: true
			}).then(response => {
				return response.data[0];
			}).catch(error => {
				console.error(error);
				this.$store.commit(
					"SHOW_NOTIFICATION_WITH_TYPE",
					{message: "An error occurred when trying to generate the event's sequence number."}
				);
				return null;
			});

			this.$store.dispatch("updatePlaylistEvent", {
				PlaylistID: targetEvent.PlaylistID,
				EventID: this.cuttingEvent.EventID,
				SequenceNumber: newSequenceNumber,
				StatusCode: EventStatus.Ready,
				EditCode: EditCodes.ManualReorder,
				EditComment: this.$store.getters.getUserFullName()
			}).then(() => {
				this.$store.commit(
					"SHOW_SUCCESS_NOTIFICATION",
					{message: "Event successfully pasted!"}
				);
			}).catch((error) => {
				console.error(error);
				this.$store.commit(
					"SHOW_NOTIFICATION_WITH_TYPE",
					{message: "An error occurred when trying to paste the event."}
				);
			}).finally(() => {
				this.$store.commit("setCuttingEvent", {...this.cuttingEvent, blink: false});
			});
		},

		pasteAfterCopy(targetEvent) {
			this.$store.commit("setBlinkingEvent", {
				eventID: targetEvent.EventID,
				status: true
			});

			// Copy a spot block
			if (this.copiedEvent.EventType === EventTypes.SpotBlock) {
				this.$store.dispatch("createPlaylist", {
					StationID: this.selectedStationID,
					OrganizationID: this.$store.getters.getOrgID(),
					Title: `Manually Inserted Spot Block - ${this.$store.getters.getUserFullName()}`,
					PlaylistType: this.copiedEvent?.BlockType === BlockType.NetworkSpotBlock ?
					              PlaylistTypes.NetworkSpotBlock : PlaylistTypes.SpotBlock,
					Priority: 99,
					CreatedDateTime: moment.tz(
						moment().format("YYYY-MM-DD HH:mm:ss"),
						this.stationTimezone
					),
					Events: []
				}).then(newSpotBlockPlaylist => {
					let eventListAddPromises = [];

					if (this.copiedEvent.ChildPlaylist?.Events?.length) {
						this.copiedEvent.ChildPlaylist.Events.forEach(event => {
							const newSpot = this.generatePlaylistEvent({
								...event,
								PlaylistID: newSpotBlockPlaylist.PlaylistID
							});

							eventListAddPromises.push(this.$store.dispatch("createPlaylistEvent", newSpot));
						});
					}

					Promise.all(eventListAddPromises).then(async () => {
						const newSpotBlockEvent = this.generatePlaylistEvent({
							...this.copiedEvent,
							PlaylistID: targetEvent.PlaylistID,
							ChildPlaylistID: newSpotBlockPlaylist.PlaylistID
						});

						this.createPlaylistEvent(newSpotBlockEvent, targetEvent);
					}).catch(() => {
						this.$store.commit("setBlinkingEvent", {
							eventID: targetEvent.EventID,
							status: false
						});
						this.$store.commit(
							"SHOW_NOTIFICATION_WITH_TYPE",
							{message: "An error occurred when trying to create the event."}
						);
					});
				}).catch(() => {
					this.$store.commit("setBlinkingEvent", {
						eventID: targetEvent.EventID,
						status: false
					});
					this.$store.commit(
						"SHOW_NOTIFICATION_WITH_TYPE",
						{message: "An error occurred when trying to create the event."}
					);
				});
			}
			else {
				const newEvent = this.generatePlaylistEvent({
					...this.copiedEvent,
					PlaylistID: targetEvent.PlaylistID
				});

				this.createPlaylistEvent(newEvent, targetEvent);
			}
		},

		generatePlaylistEvent(data) {
			const newEventAttributes = [
				"PlaylistID", "SequenceNumber", "EventType", "Chain", "AssetID", "AssetTypeID", "HardTime", "EtmType",
				"Description", "ChildPlaylistID", "BlockType", "StationID", "ParentScheduledStartDateTime"
			];

			let playlistEvent = _.omitBy(_.pick(data, newEventAttributes), attr => _.isUndefined(attr));    // keep only the values from the newEventAttributes array, omitting the undefined ones
			playlistEvent.StatusCode = EventStatus.Ready;
			playlistEvent.Skipped = false;

			return playlistEvent;
		},

		async createPlaylistEvent(newEvent, targetEvent, afterEvent = true) {
			newEvent.SequenceNumber = (await this.$store.dispatch("generateSequenceNumber", {
				eventID: targetEvent.EventID,
				afterEvent: afterEvent
			})).data[0];

			this.$store.dispatch("createPlaylistEvent", newEvent).then(() => {
				this.$store.commit(
					"SHOW_SUCCESS_NOTIFICATION",
					{message: "Event successfully pasted!"}
				);
			}).catch(() => {
				this.$store.commit(
					"SHOW_NOTIFICATION_WITH_TYPE",
					{message: "An error occurred when trying to paste the event."}
				);
			}).finally(() => {
				this.$store.commit("setBlinkingEvent", {
					eventID: targetEvent.EventID,
					status: false
				});
			});
		}
	},

	watch: {
		"contextMenu.open"(open) {
			if (open) {
				if (this.cuttingEvent) {
					this.$store.commit("setCuttingEvent", {...this.cuttingEvent, pasteBeforeEvent: this.event.EventID});
				}
				if (this.copiedEvent) {
					this.$store.commit("setCopiedEvent", {...this.copiedEvent, pasteBeforeEvent: this.event.EventID});
				}
			}
		}
	}
}

export default cutCopyPasteMixin;